// Client-side search / category filter / sort / pagination for the
// Company Profile products section. Pure so it can be unit-tested.

export interface ProfileProduct {
  id: string
  name: string
  description?: string | null
  categories?: string[]
  subcategories?: { area?: string | null }[]
}

export type ProductSort = 'az' | 'za'

export const PAGE_SIZE = 10

/** Top-level category: the first subcategory's area, else the first legacy category. */
export function topCategory(p: ProfileProduct): string {
  return p.subcategories?.find(s => s.area)?.area || p.categories?.[0] || ''
}

export function productCategories(products: ProfileProduct[]): string[] {
  return [...new Set(products.map(topCategory).filter(Boolean))].sort((a, b) => a.localeCompare(b))
}

export function filterProducts(
  products: ProfileProduct[],
  opts: { query?: string; category?: string; sort?: ProductSort },
): ProfileProduct[] {
  const q = (opts.query || '').trim().toLowerCase()
  const out = products.filter(p =>
    (!opts.category || topCategory(p) === opts.category) &&
    (!q || p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)),
  )
  out.sort((a, b) => a.name.localeCompare(b.name))
  if (opts.sort === 'za') out.reverse()
  return out
}

/** Slice a 1-based page; page is clamped into range. */
export function paginate<T>(items: T[], page: number, size = PAGE_SIZE) {
  const pages = Math.max(1, Math.ceil(items.length / size))
  const current = Math.min(Math.max(1, page), pages)
  const start = (current - 1) * size
  const rows = items.slice(start, start + size)
  return {
    rows,
    page: current,
    pages,
    from: rows.length ? start + 1 : 0,
    to: start + rows.length,
    total: items.length,
  }
}
